import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
    const navigate = useNavigate();
    const [remember, setRemember] = useState(false);
    const [usernameOrEmail, setUsernameOrEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    // Re-render lucide icons if present (keeps your SPA fix)
    useEffect(() => {
        if ((window as any).lucide?.createIcons) {
            try { (window as any).lucide.createIcons(); } catch (e) { }
        }
    }, []); // Only run once on mount

    // Load remembered ID
    useEffect(() => {
        const saved = localStorage.getItem("remember_id");
        if (saved) {
            setUsernameOrEmail(saved);
            setRemember(true);
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const res = await fetch(`${apiUrl}/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ usernameOrEmail, password }),
            });
            const data = await res.json();

            if (!res.ok) {
                setError(data.message || "Invalid credentials");
                return;
            }

            // Save token
            localStorage.setItem("token", data.token);

            // ✅ Normalize role here
            let role = data?.user?.role;
            if (typeof role === "object" && role?.role) {
                role = role.role; // flatten object { role: "ADMIN", permission: [...] }
            }

            if (!role) {
                setError("Role missing in response");
                return;
            }

            localStorage.setItem("role", role);

            // Remember ID if chosen
            if (remember) {
                localStorage.setItem("remember_id", usernameOrEmail);
            } else {
                localStorage.removeItem("remember_id");
            }

            // Redirect based on role
            if (role === "SUPERADMIN") {
                navigate("/clients");
            } else if (role === "ADMIN") {
                navigate("/projects");
            } else {
                setError("Unauthorized role");
            }
        } catch (err) {
            console.error(err);
            setError("Something went wrong. Please try again later");
        }
    };


    return (
        <>
            {/* LEFT panel */}
            <div className="container grid grid-cols-12 px-5 py-10 sm:px-10 sm:py-14 md:px-36 lg:h-screen lg:max-w-[1550px] lg:py-0 lg:pl-14 lg:pr-12 xl:px-24 2xl:max-w-[1750px]">
                <div className="relative z-50 h-full col-span-12 p-7 sm:p-14 bg-white rounded-2xl lg:bg-transparent lg:pr-10 lg:col-span-5 xl:pr-24 2xl:col-span-4 lg:p-0 before:content-[''] before:absolute before:inset-0 before:-mb-3.5 before:bg-white/40 before:rounded-2xl before:mx-5">
                    <div className="relative z-10 flex flex-col justify-center w-full h-full py-2 lg:py-32">
                        {/* Logo box */}
                        <div className="flex h-[55px] w-[55px] items-center justify-center rounded-[0.8rem] border border-primary/30">
                            <div className="relative flex h-[50px] w-[50px] items-center justify-center rounded-[0.6rem] bg-white bg-gradient-to-b from-theme-1/90 to-theme-2/90">
                                <div className="relative h-[26px] w-[26px] -rotate-45 [&_div]:bg-white">
                                    <div className="absolute inset-y-0 left-0 my-auto h-[75%] w-[20%] rounded-full opacity-50"></div>
                                    <div className="absolute inset-0 m-auto h-[120%] w-[20%] rounded-full"></div>
                                    <div className="absolute inset-y-0 right-0 my-auto h-[75%] w-[20%] rounded-full opacity-50"></div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-10">
                            <div className="text-2xl font-medium">Sign In</div>
                            <div className="mt-2.5 text-slate-600">
                                Don&apos;t have an account?
                                <a className="font-medium text-primary" href="#">
                                    {" "}Sign Up
                                </a>
                            </div>

                            {/* Welcome alert */}
                            <div role="alert" className="alert relative border rounded-md px-5 py-4 border-primary text-primary dark:border-primary my-7 flex items-center rounded-[0.6rem] border-primary/20 bg-primary/5 px-4 py-3 leading-[1.7]">
                                <div>
                                    <i data-lucide="lightbulb" className="mr-2 h-7 w-7 fill-primary/10 stroke-[0.8]"></i>
                                </div>
                                <div className="ml-1 mr-8">
                                    Welcome to <span className="font-medium">ApSales BA!</span> Simply click{" "}
                                    <span className="font-medium">Sign In</span> to explore and access the admin portal.
                                </div>
                                <button
                                    type="button"
                                    aria-label="Close"
                                    className="py-2 px-3 absolute right-0 my-auto mr-2 btn-close text-primary"
                                    onClick={(e) => {
                                        const el = (e.currentTarget as HTMLButtonElement).closest(".alert") as HTMLElement | null;
                                        if (el) el.style.display = "none";
                                    }}
                                >
                                    <i data-lucide="x" className="stroke-[1] w-5 h-5"></i>
                                </button>
                            </div>

                            {/* Form */}
                            <div className="mt-6">
                                <form onSubmit={handleSubmit}>
                                    <label className="inline-block mb-2">Email or Username*</label>
                                    <input
                                        type="text"
                                        placeholder="Email or Username"
                                        value={usernameOrEmail}
                                        onChange={(e) => setUsernameOrEmail(e.target.value)}
                                        required
                                        className="transition duration-200 ease-in-out w-full text-sm shadow-sm placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40 block rounded-[0.6rem] border-slate-300/80 px-4 py-3.5"
                                    />

                                    <label className="inline-block mb-2 mt-4">Password*</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="************"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="transition duration-200 ease-in-out w-full text-sm shadow-sm placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40 block rounded-[0.6rem] border-slate-300/80 px-4 py-3.5 pr-12"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                        </button>
                                    </div>

                                    <div className="flex mt-4 text-xs text-slate-500 sm:text-sm">
                                        <div className="flex items-center mr-auto">
                                            <input
                                                type="checkbox"
                                                id="remember-me"
                                                checked={remember}
                                                onChange={(e) => setRemember(e.target.checked)}
                                                className="transition-all duration-100 ease-in-out shadow-sm border-slate-200 cursor-pointer rounded focus:ring-4 focus:ring-offset-0 focus:ring-primary focus:ring-opacity-20 mr-2.5 border"
                                            />
                                            <label className="cursor-pointer select-none" htmlFor="remember-me">Remember me</label>
                                        </div>
                                        <button type="button" className="text-primary" onClick={() => alert("TODO: implement Forgot Password")}>
                                            Forgot Password?
                                        </button>
                                    </div>

                                    {error && (
                                        <div className="mt-4 rounded-md px-4 py-3 bg-red-50 text-red-600 border border-red-200">
                                            {error}
                                        </div>
                                    )}

                                    <div className="mt-5 text-center xl:mt-8 xl:text-left">
                                        <button
                                            type="submit"
                                            className="transition duration-200 border shadow-sm inline-flex items-center justify-center px-3 font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 rounded-full w-full bg-primary border-primary text-white py-3.5"
                                        >
                                            Sign In
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT background panel */}
            <div className="container fixed inset-0 grid h-screen w-screen grid-cols-12 pl-14 pr-12 lg:max-w-[1550px] xl:px-24 2xl:max-w-[1750px]">
                <div className="relative h-screen col-span-12 lg:col-span-5 2xl:col-span-4 z-20 after:bg-white after:hidden after:lg:block after:content-[''] after:absolute after:right-0 after:inset-y-0 after:bg-gradient-to-b after:from-white after:to-slate-100/80 after:w-[800%] after:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0] before:content-[''] before:hidden before:lg:block before:absolute before:right-0 before:inset-y-0 before:my-6 before:bg-gradient-to-b before:from-white/10 before:to-slate-50/10 before:bg-white/50 before:w-[800%] before:-mr-4 before:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0]"></div>

                <div className="h-full col-span-7 2xl:col-span-8 lg:relative before:content-[''] before:absolute before:lg:-ml-10 before:left-0 before:inset-y-0 before:bg-gradient-to-b before:from-theme-1 before:to-theme-2 before:w-screen before:lg:w-[800%] after:content-[''] after:absolute after:inset-y-0 after:left-0 after:w-screen after:lg:w-[800%] after:bg-texture-white after:bg-fixed after:bg-center after:lg:bg-[25rem_-25rem] after:bg-no-repeat">
                    <div className="sticky top-0 z-10 flex-col justify-center hidden h-screen ml-16 lg:flex xl:ml-28 2xl:ml-36">
                        <div className="text-[2.6rem] font-medium leading-[1.4] text-white xl:text-5xl xl:leading-[1.2]">
                            ApSales BA <br /> Promoter Sales App
                        </div>
                        <div className="mt-5 text-base leading-relaxed text-white/70 xl:text-lg">
                            ApSales is the field management tool developed to digitalize sales and marketing operations,
                            with superb Real-Time features for sales, promoter, merchandiser and retail audit.
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
